<template>
    <div>
        <TextField
            :id="id"
            ref="field"
            append-icon="$calendarDay"
            class="date-field"
            inputmode="numeric"
            v-bind="$attrs"
            :mask="dateMask"
            :required="required"
            :value="inputVal"
            :validations="validations"
            :disabled="disabled"
            @focus="onfocus"
            @blur="onblur"
            @field-model-change="onFieldModelChange"
            @input="updateFromInput"
        >
            <template #label><slot name="label">{{$attrs.label}}</slot></template>

            <template #message="messageProps">
                <slot name="message" :message="messageProps.message" />
            </template>
        </TextField>

        <Popper
            v-if="popperTarget"
            ref="popper"
            placement="bottom-end"
            scroll-container=".v-application"
            triggers="click"
            :offset="[0, 2]"
            :target="popperTarget"
            @show="onPopoverShow"
        >
            <datepicker
                :id="`${id}-picker`"
                ref="datepicker"
                input-class=""
                :highlighted="highlighted"
                :inline="true"
                :value="value"
                :minimum-view="minimumPickerView"
                @selected="updateFromPicker"
            />
        </Popper>
    </div>
</template>
<script>
    import masks from "@/util/mask"
    import datepicker from "vuejs-datepicker"
    import FieldProxyMixin from "@/components/form/FieldProxyMixin"
    import dayjs from "dayjs"

    export default {
        components: {
            datepicker,
        },

        mixins: [FieldProxyMixin],

        inheritAttrs: true,

        data() {
            return {
                dateMask: null,
                invalid: false,
                popperTarget: null,
                textVal: null,
                highlighted: {
                    dates: [new Date()],
                },
            }
        },

        props: {
            dateStyle: {
                type: String,
                default: "date",
                validator: function(value) {
                    return ["date", "monthYear", "monthYear2"].includes(value)
                },
            },

            min: {
                type: [Date, String]
            },

            max: {
                type: [Date, String]
            }
        },

        computed: {
            mask() {
                switch (this.dateStyle) {
                    case "monthYear":
                        return masks.monthYear
                    case "monthYear2":
                        return masks.monthYear2
                    default:
                        return masks.localDate
                }
            },

            minimumPickerView() {
                switch (this.dateStyle) {
                    case "monthYear":
                        return "month"
                    case "monthYear2":
                        return "month"
                    default:
                        return "day"
                }
            },

            inputFormat() {
                switch (this.dateStyle) {
                    case "monthYear":
                        return "MM/YYYY"
                    case "monthYear2":
                        return "MM/YY"
                    default:
                        return "MM/DD/YYYY"
                }

            },

            inputRegex() {
                switch (this.dateStyle) {
                    case "monthYear":
                        return /^\d{1,2}\/\d{4}$/
                    case "monthYear2":
                        return /^\d{1,2}\/\d{2}$/
                    default:
                        return /^\d{1,2}\/\d{1,2}\/\d{4}$/
                }
            },

        },

        created() {
            this.dateMask = { ...this.mask, lazy: true }
        },

        mounted() {
            this.invalid = this.$refs.field.invalid
            this.popperTarget = this.$refs.field.$el.querySelector(".v-icon")
            this.updateClear()
        },

        methods: {
            clear() {
                this.$refs.field.$el.focus()
                this.$emit("input", null)
                setTimeout(() => this.$refs.field.$el.blur())
            },

            fromModel(val) {
                if (!val) {
                    return null
                }

                return dayjs(val).format(this.inputFormat)
            },

            toModel(val) {
                if (val instanceof Date) {
                    return val
                }

                return this.parseDate(val) || null
            },

            onFieldModelChange(field) {
                this.invalid = field.invalid
            },

            onPopoverShow() {
                this.$refs.datepicker.showDayCalendar()
            },

            parseDate(val) {
                val = (val || "").trim()

                if (!val) {
                    return null
                }

                if (!this.inputRegex.test(val)) {
                    return false
                }

                const djsDate = dayjs(val, this.inputFormat)

                if (!djsDate.isValid()) {
                    return false
                }

                return djsDate.toDate()
            },

            updateClear() {
                const val = this.$refs.field ? this.$refs.field.$el.value : null

                if (this.$refs.clear) {
                    if (val && val !== this.placeholder) {
                        this.$refs.clear.classList?.remove("invisible")
                    } else {
                        this.$refs.clear.classList?.add("invisible")
                    }
                }
            },

            updateFromPicker(date) {
                this.$refs.popper.hide()
                setTimeout(() => {
                    this.$emit("input", date)

                    if (this.$refs.field) {
                        setTimeout(() => {
                            this.$refs.field.element.value = this.fromModel(this.value)
                            this.$refs.field.element.dispatchEvent(new Event("input"))
                            this.$refs.field.$emit("validate")
                        })
                    }
                })
            },

            updateFromInput(value) {
                const date = this.parseDate(value)

                if (date !== false) {
                    this.$emit("input", date)
                }

                setTimeout(() => this.updateClear())
            },

            onfocus() {
                this.dateMask = { ...this.dateMask, lazy: false }
            },

            onblur(event) {
                const modelValue = this.toModel(event.target.value)
                event.target.value = this.fromModel(modelValue)
                event.target.dispatchEvent(new Event("input"))
                this.$refs.popper.hide()

                setTimeout(() => {
                    this.$emit("input", modelValue)
                    this.dateMask = { ...this.dateMask, lazy: true }

                    setTimeout(() => {
                        // this.dateMask = { ...this.dateMask, lazy: true }
                        this.updateClear()
                    })
                })
            },

            validationSource: function (target, field) {
                const min = toDate(this.min)

                if (min) {
                    field.applyValidator(target, "minDate", min)
                }

                const max = toDate(this.max)

                if (max) {
                    field.applyValidator(target, "maxDate", max)
                }
            }
        },
    }

    function toDate(val) {
        return val == null || val instanceof Date ? val : dayjs(val).toDate()
    }
</script>

<style lang="scss">
    .date-field .v-icon {
        cursor: pointer;
    }
</style>
